/**
 * Úvodní krok: zadání vlastními slovy a podklady.
 *
 * Klient tu popíše, co od ocenění potřebuje (prodej, vstup investora,
 * vyrovnání společníků…), nahraje účetní závěrky a ke každé může připsat,
 * co je na ní potřeba vědět. Zadání a komentáře se ukládají s valuací
 * a dostane je i AI zpráva. Závěrky převezme krok Finance, takže se
 * nenahrávají dvakrát.
 *
 * Po odeslání proběhne rozbor: závěrky přečte parser výkazů, server
 * sestaví dotazy z pevného katalogu a předběžně zvolí metodu. Odpovědi
 * se propíšou do dalších kroků, kde je klient ještě uvidí.
 *
 * Krok je dobrovolný. Kdo podklady nemá, pokračuje ručním zadáním čísel
 * stejně jako dřív.
 */
import React, { useEffect, useRef, useState } from 'react';
import { validatePDFFile } from '../api/pdfParser';
import { posunNaChybu } from '../utils/chyby';
import { ulozSoubor, odeberSoubor, souborPodleId, souboryZeZadani } from '../utils/podklady';
import { spustRozbor, vyhodnotRozbor, prevezmiOdpovedi } from '../api/rozbor';
import { RozborPanel } from './RozborPanel';

const MAX_SOUBORU = 10;
const MAX_ZADANI = 8000;
const MAX_KOMENTARE = 1000;

export function Step_Zadani({ wizardState, setWizardState, onNext }) {
  const puvodni = wizardState.zadani || {};
  const [text, setText] = useState(puvodni.text || '');
  // Popisy souborů z uloženého stavu. Po obnovení stránky už soubor sám
  // v prohlížeči není, komentář ale zůstává a soubor jde nahrát znovu.
  const [soubory, setSoubory] = useState(puvodni.soubory || []);
  const [chyba, setChyba] = useState(null);
  const [tazeni, setTazeni] = useState(false);

  // Rozbor podkladů. Po návratu do kroku se ukáže uložený rozbor.
  const [rozbor, setRozbor] = useState(puvodni.rozbor || null);
  const [odpovedi, setOdpovedi] = useState((puvodni.rozbor && puvodni.rozbor.odpovedi) || {});
  const [citace, setCitace] = useState((puvodni.rozbor && puvodni.rozbor.citace) || {});
  const [faze, setFaze] = useState(puvodni.rozbor ? 'rozbor' : 'zadani');
  const [nacitam, setNacitam] = useState(false);
  const [prepocitavam, setPrepocitavam] = useState(false);
  const [chybejiciPovinne, setChybejiciPovinne] = useState([]);
  const casovac = useRef(null);
  useEffect(() => () => clearTimeout(casovac.current), []);

  const pridej = (incoming) => {
    const chyby = [];
    const nove = [];
    for (const f of incoming) {
      const v = validatePDFFile(f);
      if (!v.valid) { chyby.push(`${f.name}: ${v.error}`); continue; }
      const id = ulozSoubor(f);
      nove.push({ id, name: f.name, size: f.size, komentar: '' });
    }
    setSoubory(prev => {
      const sloucene = [...prev];
      for (const s of nove) {
        const i = sloucene.findIndex(x => x.id === s.id);
        if (i === -1) sloucene.push(s);
        else sloucene[i] = { ...sloucene[i], ...s, komentar: sloucene[i].komentar };
      }
      if (sloucene.length > MAX_SOUBORU) {
        chyby.push(`Nahrát lze nejvýše ${MAX_SOUBORU} souborů.`);
      }
      return sloucene.slice(0, MAX_SOUBORU);
    });
    setChyba(chyby.length ? chyby.join(' ') : null);
  };

  const odeber = (id) => {
    odeberSoubor(id);
    setSoubory(prev => prev.filter(s => s.id !== id));
  };

  const komentuj = (id, hodnota) => {
    setSoubory(prev => prev.map(s => (s.id === id ? { ...s, komentar: hodnota } : s)));
  };

  const uloz = (cesta) => {
    setWizardState(prev => ({
      ...prev,
      zadani: {
        text: text.trim(),
        soubory: soubory.map(({ id, name, size, komentar }) => ({ id, name, size, komentar: (komentar || '').trim() })),
        cesta,
      },
    }));
    onNext();
  };

  const popisSouboru = () => soubory.map(({ id, name, size, komentar }) =>
    ({ id, name, size, komentar: (komentar || '').trim() }));

  const pokracovat = async () => {
    if (!text.trim() && !soubory.length) {
      setChyba('Napište zadání nebo nahrajte aspoň jednu závěrku. Pokud podklady nemáte, zvolte ruční zadání.');
      posunNaChybu();
      return;
    }
    setNacitam(true);
    setChyba(null);
    try {
      const zadani = { text: text.trim(), soubory: popisSouboru() };
      const vysledek = await spustRozbor({
        soubory: souboryZeZadani(zadani),
        zadani,
        typPodnikani: wizardState.typ_podnikani,
      });
      const nalezene = {};
      const cit = {};
      (vysledek.dotazy || []).forEach(d => {
        if (d.odpoved !== null && d.odpoved !== undefined) nalezene[d.klic] = d.odpoved;
        if (d.citace) cit[d.klic] = d.citace;
      });
      setRozbor(vysledek);
      setOdpovedi(nalezene);
      setCitace(cit);
      setChybejiciPovinne([]);
      setFaze('rozbor');
      window.scrollTo({ top: 0, behavior: 'auto' });
    } catch (e) {
      setChyba(`Rozbor podkladů se nepodařil (${e.message}). Můžete pokračovat bez něj, čísla zadáte v dalších krocích.`);
    } finally {
      setNacitam(false);
    }
  };

  const zmenOdpoved = (klic, hodnota) => {
    const nove = { ...odpovedi };
    if (hodnota === null || hodnota === undefined || hodnota === '') delete nove[klic];
    else nove[klic] = hodnota;
    setOdpovedi(nove);
    // Klient odpověď změnil, citace ze zadání už k ní nepatří.
    if (citace[klic]) setCitace(prev => { const c = { ...prev }; delete c[klic]; return c; });
    setChybejiciPovinne(prev => prev.filter(k => k !== klic));
    clearTimeout(casovac.current);
    casovac.current = setTimeout(async () => {
      setPrepocitavam(true);
      try {
        const v = await vyhodnotRozbor({ vykaz: rozbor.vykaz, typPodnikani: wizardState.typ_podnikani, odpovedi: nove });
        setRozbor(prev => ({ ...prev, dotazy: v.dotazy, metoda: v.metoda, chybi: v.chybi, navrh: v.navrh }));
      } catch (e) {
        setChyba(`Přepočet se nepodařil (${e.message}).`);
      } finally {
        setPrepocitavam(false);
      }
    }, 400);
  };

  const potvrdit = () => {
    const chybi = (rozbor.dotazy || [])
      .filter(d => d.povinny && (odpovedi[d.klic] === undefined || odpovedi[d.klic] === null || odpovedi[d.klic] === ''))
      .map(d => d.klic);
    if (chybi.length) {
      setChybejiciPovinne(chybi);
      posunNaChybu();
      return;
    }
    // Hodnoty odpovědí, které z katalogu po přepočtu vypadly, se neukládají.
    const platne = new Set((rozbor.dotazy || []).map(d => d.klic));
    const finalni = Object.fromEntries(Object.entries(odpovedi).filter(([k]) => platne.has(k)));
    setWizardState(prev => prevezmiOdpovedi({
      ...prev,
      zadani: {
        text: text.trim(),
        soubory: popisSouboru(),
        cesta: 'podklady',
        rozbor: { ...rozbor, odpovedi: finalni, citace },
      },
    }, finalni));
    onNext();
  };

  const chybiSoubor = (s) => !souborPodleId(s.id);

  if (faze === 'rozbor' && rozbor) {
    return (
      <div className="step-container">
        <h2>Rozbor podkladů</h2>
        <p className="step-description">
          Z vašich podkladů jsme přečetli, co šlo, a předběžně zvolili metodu
          ocenění. Doplňte prosím odpovědi na dotazy níže. Čísla, která se ze
          závěrek nepodařilo přečíst, nedopočítáváme, zadáte nebo potvrdíte je
          v dalších krocích.
        </p>
        <RozborPanel
          rozbor={rozbor}
          odpovedi={odpovedi}
          citace={citace}
          chybejiciPovinne={chybejiciPovinne}
          onZmena={zmenOdpoved}
          prepocitavam={prepocitavam}
        />
        {chyba && <div className="error-message chyba-pole">{chyba}</div>}
        <div className="button-group">
          <button type="button" className="btn-secondary" onClick={() => setFaze('zadani')}>
            Upravit zadání a podklady
          </button>
          <button type="button" className="btn-primary" onClick={potvrdit} disabled={prepocitavam}>
            Potvrdit a pokračovat
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="step-container">
      <h2>Zadání a podklady</h2>
      <p className="step-description">
        Popište vlastními slovy, k čemu ocenění potřebujete a co je na firmě
        důležité vědět. Pak nahrajte účetní závěrky. Ke každé můžete připsat
        komentář, například že v daném roce byla jednorázová zakázka nebo
        prodej majetku. Z podkladů vybereme vhodnou metodu ocenění a zeptáme
        se jen na to, co v nich chybí.
      </p>

      <div className="edit-form">
        <div className="form-group">
          <label htmlFor="zadani-text">Zadání vlastními slovy</label>
          <textarea
            id="zadani-text"
            rows={8}
            maxLength={MAX_ZADANI}
            value={text}
            onChange={e => { setText(e.target.value); if (chyba) setChyba(null); }}
            placeholder="Například: Chceme prodat 50 % firmy společníkovi. Firma vyrábí nábytek na zakázku, majitel v ní pracuje na plný úvazek. V roce 2024 jsme prodali starou halu, proto je ten rok ziskovější."
          />
          <small>{text.length} / {MAX_ZADANI} znaků</small>
        </div>

        <div className="form-group">
          <label>Účetní závěrky (volitelné)</label>
          <input
            type="file"
            accept=".pdf"
            multiple
            id="zadani-pdf"
            style={{ display: 'none' }}
            onChange={e => { pridej(Array.from(e.target.files || [])); e.target.value = ''; }}
          />
          <label
            htmlFor="zadani-pdf"
            onDragOver={e => { e.preventDefault(); setTazeni(true); }}
            onDragLeave={() => setTazeni(false)}
            onDrop={e => {
              e.preventDefault();
              setTazeni(false);
              pridej(Array.from(e.dataTransfer.files || []).filter(f => f.type === 'application/pdf'));
            }}
            style={{
              display: 'block',
              padding: '2rem',
              border: `2px dashed ${tazeni ? 'var(--purple-glow)' : 'var(--purple-mid)'}`,
              borderRadius: 12,
              textAlign: 'center',
              cursor: 'pointer',
              background: 'var(--purple-deep)',
              fontWeight: 400,
            }}
          >
            <div style={{ fontWeight: 600, fontSize: '1rem', color: 'var(--star-white)' }}>
              {tazeni ? 'Pusťte soubory sem' : 'Klikněte pro výběr nebo přetáhněte PDF sem'}
            </div>
            <div style={{ fontSize: '0.85rem', color: 'var(--text-dim)', marginTop: '0.3rem' }}>
              rozvaha, výkaz zisku a ztráty, příloha · nejvýše {MAX_SOUBORU} souborů po 10 MB
            </div>
          </label>
        </div>

        {soubory.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginBottom: '1rem' }}>
            {soubory.map(s => (
              <div key={s.id} style={{ background: 'var(--purple-deep)', borderRadius: 10, padding: '0.75rem 0.9rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.5rem' }}>
                  <span style={{ fontSize: '0.9rem', color: 'var(--star-white)', fontWeight: 500, wordBreak: 'break-word' }}>
                    {s.name} <span style={{ color: 'var(--text-dim)', fontWeight: 400 }}>({Math.round(s.size / 1024)} kB)</span>
                  </span>
                  <button
                    type="button"
                    onClick={() => odeber(s.id)}
                    aria-label={`Odebrat ${s.name}`}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-danger)', fontWeight: 700, fontSize: '1.1rem', lineHeight: 1 }}
                  >×</button>
                </div>
                {chybiSoubor(s) && (
                  <small style={{ display: 'block', color: 'var(--text-dim)', marginTop: '0.25rem' }}>
                    Soubor už není v prohlížeči načtený. Komentář zůstal, pro čtení čísel ho nahrajte znovu.
                  </small>
                )}
                <div className="form-group" style={{ margin: '0.5rem 0 0' }}>
                  <textarea
                    rows={2}
                    maxLength={MAX_KOMENTARE}
                    value={s.komentar}
                    onChange={e => komentuj(s.id, e.target.value)}
                    aria-label={`Komentář k ${s.name}`}
                    placeholder="Komentář k tomuto dokumentu (volitelné), například co je v něm mimořádné nebo neúplné"
                  />
                </div>
              </div>
            ))}
          </div>
        )}

        {chyba && <div className="error-message chyba-pole">{chyba}</div>}
      </div>

      <div className="button-group">
        <button type="button" className="btn-secondary" onClick={() => uloz('rucne')}>
          Nemám podklady, zadám čísla ručně
        </button>
        {chyba && chyba.startsWith('Rozbor podkladů se nepodařil') && (
          <button type="button" className="btn-secondary" onClick={() => uloz('podklady')}>
            Pokračovat bez rozboru
          </button>
        )}
        <button type="button" className="btn-primary" onClick={pokracovat} disabled={nacitam}>
          {nacitam ? 'Procházím podklady…' : 'Pokračovat'}
        </button>
      </div>
    </div>
  );
}
